import { createRef } from 'react';
import {IContentBlock, IDesignBlock} from '../../types/iblock';
import { IVideoBlockData } from '../../types/content-block-data';
import { escapeHTML } from '../../lib/page-builder-lib';
import pageBuilderStore from '../../stores/pageBuilderStore';
import blocksManager from '../../stores/blocks-manager';
import contentManager from '../../stores/content-manager';

/**
 * Converts a VideoBlock (IContentBlock) to an HTML string.
 * Renders as a responsive <iframe> for embedding (e.g., YouTube, Vimeo).
 *
 * @param block - The IContentBlock (should be blockType "video")
 * @returns HTML string (e.g., <div class="video-block"><iframe ...></iframe></div>)
 * 
 * @remarks
 * - Added a new local variable `isEmail` for toggling serialization markup.
 */
export function fromVideoBlockToHTML(block: IContentBlock): string {
  if (block.blockType !== "video") return "";

  const isEmail = pageBuilderStore.getIsEmail();
  const data = block.blockData as IVideoBlockData;
  const isPreview = pageBuilderStore.previewMode?.showPreview;
  
  const url = escapeHTML(data.url ?? "");
  return `<div class="${block.selector}" ${isPreview && `style="padding: ${data.padding?.value}${data.padding?.unit}; aspect-ratio: 16/9;"`}><iframe src="${url}" allowfullscreen style="width:100%;height:100%;border:none;"></iframe></div>`;
}

/**
 * Serializes a VideoBlock's data for storage or export.
 * Omits UI-only fields.
 *
 * @param block - The IContentBlock (should be blockType "video")
 * @returns A JSON-serializable object
 */
export function fromVideoBlockToJSON(block: IContentBlock): object {
  if (block.blockType !== "video") return {};
  const data = block.blockData as IVideoBlockData;
  return {
    contentBlockId: block.contentBlockId,
    blockType: block.blockType,
    selector: block.selector,
    blockData: {
      url: data.url ?? "",
    },
  };
}

/**
 * Reconstructs a VideoBlock from JSON data.
 *
 * @param json - The JSON object to parse
 * @returns An IContentBlock instance (for use in your store/UI)
 */
export function fromJSONToVideoBlock(json: any, context: "canvas" | "columns" = "canvas"): IDesignBlock {
  const videoStyles = contentManager.getStylesForSelector(json.selector);
    
  const newDesignBlockRef = createRef<HTMLDivElement>();
  const newContentBlockRef = createRef<HTMLElement>(); // generic for now

  const block: IDesignBlock = {
    designBlockId: crypto.randomUUID(), // always generate fresh
    designBlockRef: newDesignBlockRef,
    contentBlock: {
      contentBlockId: json.contentBlockId,
      blockType: json.blockType,
      contentBlockRef: newContentBlockRef,
      selector: json.selector,
      blockData: {
        url: json.blockData?.url ?? "",
        padding: blocksManager.parseDimensions(videoStyles?.["padding"], "single"),
      } as IVideoBlockData,
    } as IContentBlock,
  };

  blocksManager.addDesignTreeEntry({
    contentBlockId: block.contentBlock.contentBlockId,
    designBlockId: block.designBlockId,
    designBlockRef: newDesignBlockRef,
    contentBlockRef: newContentBlockRef,
    contentBlockType: block.contentBlock.blockType.toUpperCase(),
    location: context,
  });

  return block;
}
